import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { computed, PropType } from "vue";
import {
  UnitValue,
  UnitValueAggregate,
  UnitValueCellNavigation,
  UnitValueDetailContext,
  UnitValueDetailField,
  UnitValueRowTag,
} from "@/types/UnitValue";
import ValueCellNumberInput from "@/views/unit_values/components/ValueCellNumberInput.vue";
import { calculateFields } from "@/views/unit_values/services/unitValues";


export default /*#__PURE__*/_defineComponent({
  __name: 'ValueCells',
  props: {
  unitValue: {
    type: Object as PropType<UnitValue>,
    required: true,
  },
  tag: {
    type: Object as PropType<UnitValueRowTag>,
    required: true,
  },
  isAdminMode: {
    type: Boolean,
    required: true,
  },
  rowHeight: {
    type: Number,
    required: true,
  },
  valueCellDetailContext: {
    type: Object as PropType<UnitValueDetailContext | null>,
    required: false,
  },
  errorField: {
    type: String as PropType<string | null>,
    required: false,
  },
  showLastRightBorder: {
    type: Boolean,
    required: true,
  },
  unitValueAggregate: {
    type: Object as PropType<UnitValueAggregate>,
    required: false,
  },
},
  emits: [
  "changed",
  "detailShown",
  "detailHidden",
  "focused",
  "blurred",
  "navigated",
],
  setup(__props, { emit: __emit }) {

const props = __props;

const emit = __emit;

const fields = computed(
  () =>
    [
      "custom_adjusted_duration",
      props.isAdminMode ? "oai_adjusted_workforce" : "custom_adjusted_workforce",
      "quantity",
      "custom_adjusted_value",
    ] as (keyof UnitValue)[],
);

const refreshDetailTooltip = (unitValue: UnitValue) => {
  if (props.valueCellDetailContext) {
    emit("detailShown", {
      ...props.valueCellDetailContext,
      unitValue,
    });
  }
};

const handleNumberInputChanged = ({ name, value }: { name: string; value: number | null }) => {
  const newUnitValue: UnitValue = {
    ...props.unitValue,
    [name]: value,
  };
  const finalUnitValue: UnitValue = {
    ...newUnitValue,
    ...calculateFields(newUnitValue),
  };
  emit(`changed`, finalUnitValue);
  refreshDetailTooltip(finalUnitValue);
};

const handleShowInfo = (coordinates: number[], field: UnitValueDetailField) => {
  const context: UnitValueDetailContext = {
    unitValue: props.unitValue,
    unitValueAggregate: props.unitValueAggregate,
    field,
    x: coordinates[0],
    y: coordinates[1],
  };
  emit("detailShown", context);
};

const handleNavigated = (unitValueCellNavigation: UnitValueCellNavigation) => {
  const payload: UnitValueCellNavigation = {
    ...unitValueCellNavigation,
    fields: fields.value,
  };
  emit("navigated", payload);
};

const emitFocused = () => {
  emit("focused", props.unitValue);
};

const emitBlurred = () => {
  emit("blurred", props.unitValue);
};

const backgroundColor = computed(() => {
  if (!props.unitValueAggregate || props.unitValue.state !== "finished") {
    return null;
  }
  const { confidence_lower_limit, confidence_upper_limit } = props.unitValueAggregate;
  if (
    confidence_lower_limit === null ||
    confidence_upper_limit === null ||
    props.unitValue.value === null
  ) {
    return null;
  }
  const interval = confidence_upper_limit - confidence_lower_limit;
  const colors = ["#63be7b80", "#84c77c80", "#e2e28280", "#ffe98480", "#fdc57d80", "#f8696b80"];
  const step = interval / colors.length;
  const index = Math.floor((props.unitValue.value - confidence_lower_limit) / step);
  const finalIndex = Math.max(Math.min(index, colors.length - 1), 0);
  return colors[finalIndex];
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", {
      class: _normalizeClass(["border-r", {
      'border-b': !__props.tag.last,
    }])
    }, [
      _createVNode(ValueCellNumberInput, {
        name: "custom_adjusted_duration",
        unitValue: __props.unitValue,
        rowHeight: __props.rowHeight,
        valueCellDetailContext: __props.valueCellDetailContext,
        value: __props.unitValue.duration,
        originalValue: __props.unitValue.custom_adjusted_duration,
        hasError: __props.errorField === 'duration',
        highlightWhenZero: true,
        showClear: __props.unitValue.custom_adjusted_duration !== null,
        infoPosition: __props.unitValue.duration !== null ? 'right' : 'none',
        onChanged: handleNumberInputChanged,
        onInfoShown: _cache[0] || (_cache[0] = ($event: any) => (handleShowInfo($event, 'duration'))),
        onInfoHidden: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('detailHidden'))),
        onFocused: emitFocused,
        onBlurred: emitBlurred,
        onNavigated: handleNavigated
      }, null, 8, ["unitValue", "rowHeight", "valueCellDetailContext", "value", "originalValue", "hasError", "showClear", "infoPosition"])
    ], 2),
    _createElementVNode("div", {
      class: _normalizeClass(["border-r", {
      'border-b': !__props.tag.last,
    }])
    }, [
      (__props.isAdminMode)
        ? (_openBlock(), _createBlock(ValueCellNumberInput, {
            key: 0,
            name: "oai_adjusted_workforce",
            unitValue: __props.unitValue,
            rowHeight: __props.rowHeight,
            valueCellDetailContext: __props.valueCellDetailContext,
            value: __props.unitValue.oai_adjusted_workforce ?? __props.unitValue.workforce,
            originalValue: __props.unitValue.oai_adjusted_workforce,
            hasError: __props.errorField === 'oai_adjusted_workforce' || __props.errorField === 'workforce',
            highlightWhenZero: true,
            showClear: __props.unitValue.oai_adjusted_workforce !== null,
            infoPosition: __props.unitValue.workforce !== null ? 'right' : 'none',
            warningTriangle: true,
            onChanged: handleNumberInputChanged,
            onInfoShown: _cache[2] || (_cache[2] = ($event: any) => (handleShowInfo($event, 'workforce'))),
            onInfoHidden: _cache[3] || (_cache[3] = ($event: any) => (_ctx.$emit('detailHidden'))),
            onFocused: emitFocused,
            onBlurred: emitBlurred,
            onNavigated: handleNavigated
          }, null, 8, ["unitValue", "rowHeight", "valueCellDetailContext", "value", "originalValue", "hasError", "showClear", "infoPosition"]))
        : (_openBlock(), _createBlock(ValueCellNumberInput, {
            key: 1,
            name: "custom_adjusted_workforce",
            unitValue: __props.unitValue,
            rowHeight: __props.rowHeight,
            valueCellDetailContext: __props.valueCellDetailContext,
            value: __props.unitValue.workforce,
            originalValue: __props.unitValue.custom_adjusted_workforce,
            hasError: __props.errorField === 'workforce',
            highlightWhenZero: true,
            showClear: __props.unitValue.custom_adjusted_workforce !== null,
            infoPosition: __props.unitValue.workforce !== null ? 'right' : 'none',
            onChanged: handleNumberInputChanged,
            onInfoShown: _cache[4] || (_cache[4] = ($event: any) => (handleShowInfo($event, 'workforce'))),
            onInfoHidden: _cache[5] || (_cache[5] = ($event: any) => (_ctx.$emit('detailHidden'))),
            onFocused: emitFocused,
            onBlurred: emitBlurred,
            onNavigated: handleNavigated
          }, null, 8, ["unitValue", "rowHeight", "valueCellDetailContext", "value", "originalValue", "hasError", "showClear", "infoPosition"]))
    ], 2),
    _createElementVNode("div", {
      class: _normalizeClass(["border-r", { 'border-b': !__props.tag.last }])
    }, [
      _createVNode(ValueCellNumberInput, {
        name: "quantity",
        unitValue: __props.unitValue,
        rowHeight: __props.rowHeight,
        valueCellDetailContext: __props.valueCellDetailContext,
        value: __props.unitValue.quantity,
        originalValue: __props.unitValue.quantity,
        hasError: __props.errorField === 'quantity',
        infoPosition: "none",
        onChanged: handleNumberInputChanged,
        onFocused: emitFocused,
        onBlurred: emitBlurred,
        onNavigated: handleNavigated
      }, null, 8, ["unitValue", "rowHeight", "valueCellDetailContext", "value", "originalValue", "hasError"])
    ], 2),
    _createElementVNode("div", {
      class: _normalizeClass({
      'border-b': !__props.tag.last,
      'border-r border-r-gray-600': __props.showLastRightBorder,
    })
    }, [
      _createVNode(ValueCellNumberInput, {
        name: "custom_adjusted_value",
        unitValue: __props.unitValue,
        rowHeight: __props.rowHeight,
        valueCellDetailContext: __props.valueCellDetailContext,
        value: __props.unitValue.value,
        originalValue: __props.unitValue.custom_adjusted_value,
        hasError: __props.errorField === 'value',
        cls: "bg-gray-100",
        showClear: __props.unitValue.custom_adjusted_value !== null,
        infoPosition: __props.unitValue.value !== null || props.isAdminMode ? 'left' : 'none',
        showUnIgnore: __props.isAdminMode && __props.unitValue.ignored,
        showIgnore: __props.isAdminMode && !__props.unitValue.ignored,
        style: _normalizeStyle(`${backgroundColor.value ? `background-color: ${backgroundColor.value}` : ''}`),
        onChanged: handleNumberInputChanged,
        onInfoShown: _cache[6] || (_cache[6] = ($event: any) => (handleShowInfo($event, 'value'))),
        onInfoHidden: _cache[7] || (_cache[7] = ($event: any) => (_ctx.$emit('detailHidden'))),
        onFocused: emitFocused,
        onBlurred: emitBlurred,
        onUnIgnoreClicked: _cache[8] || (_cache[8] = ($event: any) => (
        emit('changed', {
          ...__props.unitValue,
          ignored: false,
        })
      )),
        onIgnoreClicked: _cache[9] || (_cache[9] = ($event: any) => (
        emit('changed', {
          ...__props.unitValue,
          ignored: true,
        })
      )),
        onNavigated: handleNavigated
      }, null, 8, ["unitValue", "rowHeight", "valueCellDetailContext", "value", "originalValue", "hasError", "showClear", "infoPosition", "showUnIgnore", "showIgnore", "style"])
    ], 2)
  ], 64))
}
}

})